import Classnames from 'classnames';
import { useCallback } from 'react';
import type { ChangeEvent, Dispatch, SetStateAction, VoidFunctionComponent } from 'react';
import { Button } from 'react-bootstrap';

import Link from '../../generic/Link';
import Text from '../../generic/Text';
import type { OutdatedRule } from './types';
import type { SelectOption } from '../../../types';
import COLORS from '../../../theme/colors';
import Select from '../../generic/Select/Select';
import type { DependencyOutdatedVersionPart } from '../../../../shared/model-typings';

type OutdateRulesComponentProps = {
  outdatedRule: OutdatedRule;
  setOutdatedRule: Dispatch<SetStateAction<OutdatedRule>>;
  outdatedFallbackRule: OutdatedRule;
  setOutdatedFallbackRule: Dispatch<SetStateAction<OutdatedRule>>;
  setChanged: Dispatch<SetStateAction<boolean>>;
};

const versionSelectOptions: Array<SelectOption<DependencyOutdatedVersionPart>> = [
  {
    value: 'MAJOR',
    label: 'Major',
  },
  {
    value: 'MINOR',
    label: 'Minor',
  },
  {
    value: 'PATCH',
    label: 'Patch',
  },
];

const OutdatedRulesComponent: VoidFunctionComponent<OutdateRulesComponentProps> = ({
  outdatedRule,
  setOutdatedRule,
  outdatedFallbackRule,
  setOutdatedFallbackRule,
  setChanged,
}) => {
  const setVersionPart = useCallback(({ value }: { value: DependencyOutdatedVersionPart }) => {
    setChanged(true);
    setOutdatedRule({
      ...outdatedRule,
      changed: true,
      dependencyOutdatedVersionPart: value,
    });
  }, [outdatedRule, setChanged, setOutdatedRule]);

  const setVersionDiff = useCallback(({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setChanged(true);
    setOutdatedRule({
      ...outdatedRule,
      changed: true,
      dependencyOutdatedVersionDiff: Number(value),
    });
  }, [outdatedRule, setChanged, setOutdatedRule]);

  const onToggleOutdated = useCallback(() => {
    setChanged(true);
    setOutdatedRule({
      ...outdatedRule,
      changed: true,
      enabled: !outdatedRule.enabled,
    });
  }, [outdatedRule, setChanged, setOutdatedRule]);

  const setVersionDiffFallback = useCallback(({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setChanged(true);
    setOutdatedFallbackRule({
      ...outdatedFallbackRule,
      changed: true,
      dependencyOutdatedVersionDiff: Number(value),
    });
  }, [outdatedFallbackRule, setChanged, setOutdatedFallbackRule]);

  const onToggleOutdatedFallback = useCallback(() => {
    setChanged(true);
    setOutdatedFallbackRule({
      ...outdatedFallbackRule,
      changed: true,
      enabled: !outdatedFallbackRule.enabled,
    });
  }, [outdatedFallbackRule, setChanged, setOutdatedFallbackRule]);

  // Set up the styling for inputs
  const qualityPanelClass = Classnames('outdated-rule-flex', {
    'quality-disabled': !outdatedRule.enabled,
  });
  const qualityFallbackPanelClass = Classnames('outdated-rule-flex', {
    'quality-disabled': !outdatedFallbackRule.enabled,
  });

  const buttonStyle = {
    backgroundColor: outdatedRule.enabled ? COLORS.fossaSlateBlue : false,
    color: outdatedRule.enabled ? 'white' : false,
    padding: '8px 25px',
    marginLeft: 'auto',
  };

  const buttonFallbackStyle = {
    backgroundColor: outdatedFallbackRule.enabled
      ? COLORS.fossaSlateBlue
      : false,
    color: outdatedFallbackRule.enabled ? 'white' : false,
    padding: '8px 25px',
    marginLeft: 'auto',
  };

  return (
    <div className="margin-top outdated-rule-component">
      <div className="card">
        <div className="row" style={{ marginBottom: '30px' }}>
          <div className="col-md-6">
            <Text.H3>Stale Package Prevention</Text.H3>
            <p>
              These rules will flag any dependencies that are out of date
              according to the below guidelines. If the versions involved are
              not valid{' '}
              <Link to="https://semver.org/" openInNewTab>
                semantic versions
              </Link>
              , fallback rules can be set.
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3" />
          <div className="col-md-9">
            <div className="card outdated-rule-flex">
              <div className={qualityPanelClass}>
                <p>Flag dependency when</p>
                <div style={{ margin: 'auto 10px', width: '95px' }}>
                  <Select
                    id="dependencyOutdatedVersionPartSelect"
                    options={versionSelectOptions}
                    value={versionSelectOptions.find((opt) => opt.value === outdatedRule.dependencyOutdatedVersionPart)}
                    isDisabled={!outdatedRule.enabled}
                    onChange={(e) => {
                      if (e) {
                        setVersionPart(e);
                      }
                    }}
                  />
                </div>
                <p>version is</p>
                <div style={{ margin: 'auto 10px' }}>
                  <input
                    style={{
                      zIndex: 'initial',
                      width: '50px',
                      padding: '6px',
                    }}
                    min={1}
                    type="number"
                    disabled={!outdatedRule.enabled}
                    value={
                      outdatedRule.dependencyOutdatedVersionDiff?.toString()
                    }
                    onChange={setVersionDiff}
                  />
                </div>
                <p>versions behind latest</p>
              </div>
              <Button
                onClick={onToggleOutdated}
                style={buttonStyle}
                className="pull-right btn"
              >
                {outdatedRule.enabled ? 'Enabled' : 'Disabled'}
              </Button>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3" />
          <div className="col-md-9">
            <div className="card outdated-rule-flex">
              <div className={qualityFallbackPanelClass}>
                <p>Fallback when version is</p>
                <div style={{ margin: 'auto 10px' }}>
                  <input
                    style={{
                      zIndex: 'initial',
                      width: '50px',
                      padding: '6px',
                    }}
                    min={1}
                    type="number"
                    disabled={!outdatedFallbackRule.enabled}
                    value={
                      outdatedFallbackRule
                        .dependencyOutdatedVersionDiff?.toString()
                    }
                    onChange={setVersionDiffFallback}
                  />
                </div>
                <p>versions behind latest</p>
              </div>
              <Button
                onClick={onToggleOutdatedFallback}
                style={buttonFallbackStyle}
                className="pull-right btn"
              >
                {outdatedFallbackRule.enabled
                  ? 'Enabled'
                  : 'Disabled'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutdatedRulesComponent;
